import {useState} from "react"
import {router} from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import type {
  DealDrawerPendingNextStep,
  DealStatus,
  OwnerOption,
} from "@/types/v2/conversation"
import { HealthGlyph } from "@/components/v2/deals/health-glyph"
import {
  NextStepComposer,
  type NextStepAssigneeOption,
  type NextStepComposerEmployee,
  type NextStepComposerInitialData,
} from "@/components/v2/deals/next-step-composer"
import { useOpenSourceCall } from "@/hooks/use-open-source-call"
import { SuggestionCard } from "@/components/v2/suggestions/suggestion-card"
import { methodLabel } from "@/lib/follow-up-method"
import { DeleteConfirmKebab } from "@/components/v2/delete/delete-confirm-kebab"
import { deleteRow } from "@/lib/use-row-delete"

// NEXT STEPS section rendered above the History feed
// (Paper ENU-0 / 7DD-0 / 97K-0). Lists the pending
// Activity rows for the deal:
//   - AI-suggested rows render the shared SuggestionCard
//     (CON-223): sparkle provenance crumb ("from your
//     call <when>" / "AI suggested") + Accept / Edit /
//     Dismiss pills that route through the existing
//     suggestion operations.
//   - Manual rows have a plain card with a circle
//     checkbox + method icon (WhatsApp, Call, …) + due
//     label + assignee initials.
//
// Tapping "+ Add next step" expands the composer inline
// on desktop, or hands control back to the parent via
// `onAddClick` for the mobile variant (which renders the
// composer in a bottom sheet at the page level).

interface NextStepsSectionProps {
  deal: {id: number; name: string}
  pendingNextSteps: DealDrawerPendingNextStep[]
  // "idle 3w" accent after the count in the section header
  // (CON-221, Pencil "CON-220"). Hosts thread the deal
  // payload's `idleLabel` (DealDrawerSerializer — same
  // ≥14-day rule as the kanban card); null/absent hides
  // the accent.
  idleLabel?: string | null
  // Deal status gates the red "No next step" marker on the
  // empty state: open deals carry the marker so the drawer
  // and the board agree; closed (won/lost) deals keep the
  // neutral empty state.
  dealStatus?: DealStatus
  // Mobile vs desktop densities. Mobile uses slightly
  // smaller padding to match Paper 97K-0.
  variant?: "desktop" | "mobile"
  // Required when the section needs to actually OPEN
  // the composer or fire AI accept/dismiss requests.
  // Made optional so existing read-only call sites that
  // pass nothing keep working until they wire it up.
  currentEmployee?: NextStepComposerEmployee | null
  ownerOptions?: OwnerOption[]
  // Mobile callers hoist the composer to a bottom sheet
  // owned by the page (so the scrim covers the whole
  // viewport). Set `composer="external"` and the
  // section invokes `onAddClick` instead of expanding
  // the inline composer.
  composer?: "inline" | "external"
  onAddClick?: () => void
  onEditClick?: (step: DealDrawerPendingNextStep) => void
}

export function NextStepsSection({
  deal,
  pendingNextSteps,
  idleLabel,
  dealStatus,
  variant = "desktop",
  currentEmployee,
  ownerOptions = [],
  composer = "inline",
  onAddClick,
  onEditClick,
}: NextStepsSectionProps) {
  const { t } = useLingui()
  const [composerOpen, setComposerOpen] = useState(false)
  const [editingStep, setEditingStep] =
    useState<DealDrawerPendingNextStep | null>(null)

  if (pendingNextSteps.length === 0 && !composerOpen && !editingStep) {
    return (
      <EmptySection
        variant={variant}
        dealStatus={dealStatus}
        canCreate={Boolean(currentEmployee)}
        onAddClick={() => openComposer(null)}
      />
    )
  }

  const labelClass =
    variant === "mobile"
      ? "tracking-[0.06em] uppercase font-medium text-[#9A938A] text-[11px] leading-[14px]"
      : "tracking-[0.08em] uppercase font-semibold text-[#7A746B] text-[11px] leading-3"
  const countClass =
    "tabular-nums text-[#9A938A] text-[11px] leading-[14px] font-medium"

  function openComposer(step: DealDrawerPendingNextStep | null) {
    if (composer === "external") {
      // External callers (mobile show / drawer) host the
      // composer in a bottom-sheet at the page level. Add
      // and Edit are two different sheets / pre-fills, so
      // route them through distinct callbacks instead of
      // funneling everything through `onAddClick`. Falls
      // back to `onAddClick` for Edit only when the caller
      // didn't wire `onEditClick` — that way an edit on a
      // host that only knows how to open the composer
      // blank still produces a reasonable affordance
      // rather than silently doing nothing.
      if (step) {
        if (onEditClick) {
          onEditClick(step)
        } else {
          onAddClick?.()
        }
      } else {
        onAddClick?.()
      }
      return
    }
    if (step) {
      setEditingStep(step)
      setComposerOpen(true)
      onEditClick?.(step)
    } else {
      setEditingStep(null)
      setComposerOpen(true)
    }
  }

  const initial: NextStepComposerInitialData | undefined =
    editingStep
      ? {
          id: editingStep.id,
          title: editingStep.title ?? "",
          method: editingStep.method ?? null,
          dueAtIso: editingStep.dueAtIso,
          assigneeId: editingStep.assignee?.id ?? null,
        }
      : undefined

  const assigneeOptions: NextStepAssigneeOption[] = ownerOptions.map(
    (o) => ({id: o.id, name: o.name, initials: o.initials}),
  )

  return (
    <section
      data-testid="next-steps-section"
      className="flex flex-col gap-3"
    >
      <header className="flex items-center gap-2.5">
        <h3 className={labelClass}><Trans>Next steps</Trans></h3>
        <span className={countClass}>{pendingNextSteps.length}</span>
        {(idleLabel ?? null) ? (
          <span
            data-testid="next-steps-idle"
            className="flex items-center gap-1 text-[11px] leading-[14px] shrink-0"
          >
            <span className="text-[#C9C2B6]">·</span>
            <span className="font-medium text-[#B86A2D]">
              {idleLabel}
            </span>
          </span>
        ) : null}
        <span className="grow" />
        {composerOpen ? (
          <button
            type="button"
            onClick={() => {
              setComposerOpen(false)
              setEditingStep(null)
            }}
            data-testid="next-steps-cancel"
            className="rounded-full border border-[#C9C2B6] bg-white text-foreground text-xs leading-4 font-medium px-2.75 py-1.5"
          >
            <Trans>Cancel</Trans>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => openComposer(null)}
            data-testid="next-steps-add"
            disabled={!currentEmployee}
            aria-label={t`Add next step`}
            className="flex items-center gap-1.25 rounded-full py-1.5 px-2.75 bg-white border border-[#C9C2B6] text-foreground text-xs leading-4 font-medium hover:bg-[#F7F5F0] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <PlusIcon />
            <Trans>Add next step</Trans>
          </button>
        )}
      </header>
      {composerOpen && currentEmployee ? (
        <NextStepComposer
          deal={deal}
          currentEmployee={currentEmployee}
          ownerOptions={assigneeOptions}
          initial={initial}
          variant={variant === "mobile" ? "sheet" : "inline"}
          onSaved={() => {
            setComposerOpen(false)
            setEditingStep(null)
            router.reload({only: ["deal"]})
          }}
          onCancel={() => {
            setComposerOpen(false)
            setEditingStep(null)
          }}
        />
      ) : null}
      {pendingNextSteps.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {pendingNextSteps.map((step) => (
            <li key={step.id}>
              <NextStepRow
                step={step}
                deal={deal}
                onEdit={() => openComposer(step)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function EmptySection({
  variant,
  dealStatus,
  canCreate,
  onAddClick,
}: {
  variant: "desktop" | "mobile"
  dealStatus?: DealStatus
  canCreate: boolean
  onAddClick: () => void
}) {
  const { t } = useLingui()
  const labelClass =
    variant === "mobile"
      ? "tracking-[0.06em] uppercase font-medium text-[#9A938A] text-[11px] leading-[14px]"
      : "tracking-[0.08em] uppercase font-semibold text-[#7A746B] text-[11px] leading-3"
  return (
    <section
      data-testid="next-steps-section-empty"
      className="flex flex-col gap-3"
    >
      <header className="flex items-center gap-2.5">
        <h3 className={labelClass}><Trans>Next steps</Trans></h3>
        {dealStatus === "open" ? (
          // Open deal with nothing scheduled — carry the
          // kanban card's red No-next-step signal into the
          // header so the drawer / show page and the board
          // agree (CON-221). Closed deals keep the neutral
          // empty state.
          <span
            data-testid="next-steps-empty-marker"
            className="flex items-center gap-1 text-[#B84F4F] shrink-0"
          >
            <HealthGlyph state="none" size={12} />
            <span className="text-[11px] leading-[14px] font-semibold">
              <Trans>No next step</Trans>
            </span>
          </span>
        ) : null}
        <span className="grow" />
        <button
          type="button"
          onClick={onAddClick}
          disabled={!canCreate}
          data-testid="next-steps-add"
          aria-label={t`Add next step`}
          className="flex items-center gap-1.25 rounded-full py-1.5 px-2.75 bg-white border border-[#C9C2B6] text-foreground text-xs leading-4 font-medium hover:bg-[#F7F5F0] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <PlusIcon />
          <Trans>Add next step</Trans>
        </button>
      </header>
      <p className="text-[#7A746B] text-[13px] leading-5">
        <Trans>No next steps yet. Add one to track what comes next on this deal.</Trans>
      </p>
    </section>
  )
}

function NextStepRow({
  step,
  deal,
  onEdit,
}: {
  step: DealDrawerPendingNextStep
  deal: {id: number; name: string}
  onEdit: () => void
}) {
  const { t, i18n } = useLingui()
  // Hooks can't be conditional — resolved here even though
  // only the AI branch's crumb consumes it (committed rows
  // resolve it again inside FromCallBreadcrumb).
  const openSourceCall = useOpenSourceCall()

  // The suggestion treatment applies only while the AI row is
  // unconfirmed — once the rep accepts (`confirmed_at`
  // stamped), it's a committed row like any other (CON-223).
  if (step.aiSuggested && !(step.confirmed ?? false)) {
    return (
      <SuggestionCard
        title={step.title ?? t`Next step`}
        crumb={
          step.voiceCallId != null && step.fromCallLabel
            ? {
                label: t`from your call ${step.fromCallLabel}`,
                onClick: openSourceCall
                  ? () => openSourceCall(step.voiceCallId!)
                  : undefined,
                testId: "next-step-from-call",
              }
            : {label: t`AI suggested`}
        }
        metaFacts={[step.dueLabel, methodLabel(step.method ?? null, i18n)]}
        onAccept={() => {
          router.post(
            `/v2/deals/${deal.id}/activities/${step.id}/suggestion_acceptance`,
            {},
            {preserveScroll: true},
          )
        }}
        onEdit={onEdit}
        onDismiss={() => {
          router.post(
            `/v2/deals/${deal.id}/activities/${step.id}/suggestion_dismissal`,
            {},
            {preserveScroll: true},
          )
        }}
        testIds={{
          root: `next-step-row-suggested-${step.id}`,
          accept: `next-step-accept-${step.id}`,
          dismiss: `next-step-dismiss-${step.id}`,
          edit: `next-step-edit-link-${step.id}`,
        }}
      />
    )
  }

  return (
    <article className="flex flex-col rounded-xl bg-white border border-[#E5DFD3] py-3 px-3.5 gap-1.5">
      <div className="flex items-start gap-3">
        <CompleteToggle deal={deal} step={step} />
        <div className="grow min-w-0 flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground text-[14px] leading-5">
              {step.title ?? t`Next step`}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-[12px] leading-4 text-[#7A746B]">
            <MethodGlyph method={step.method} />
            {step.dueLabel ? (
              // Overdue steps paint the server's "Overdue
              // 5d · Jun 1" wording amber (CON-221) so a
              // blown date doesn't hide in plain ink.
              <span
                data-testid={`next-step-due-${step.id}`}
                className={
                  (step.overdue ?? false)
                    ? "font-medium text-[#B86A2D]"
                    : "font-medium text-foreground"
                }
              >
                {step.dueLabel}
              </span>
            ) : null}
            {step.assignee ? (
              <>
                <span>·</span>
                <span>{step.assignee.initials ?? step.assignee.name}</span>
              </>
            ) : null}
          </div>
        </div>
        <DeleteConfirmKebab
          promptKind="follow-up"
          quote={step.title}
          ariaLabel={t`Next step options`}
          testId={`next-step-edit-${step.id}`}
          extraActions={[
            {
              key: "edit",
              label: t`Edit`,
              icon: <EditPencilIcon />,
              onSelect: onEdit,
            },
          ]}
          onConfirmDelete={async () => {
            const ok = await deleteRow(
              `/v2/deals/${deal.id}/activities/${step.id}`,
              t`Follow-up deleted`,
            )
            if (ok) router.reload({ only: ["deal"] })
            return ok
          }}
        />
      </div>
      {step.voiceCallId != null && step.fromCallLabel ? (
        <FromCallBreadcrumb
          voiceCallId={step.voiceCallId}
          label={step.fromCallLabel}
        />
      ) : null}
    </article>
  )
}

// "✨ from your call <label>" breadcrumb on a next-step row
// born from a call (CON-195). Opens the source call —
// pushing into the drawer trail when rendered inside the
// slideover, else opening the floating viewer.
function FromCallBreadcrumb({
  voiceCallId,
  label,
}: {
  voiceCallId: number
  label: string
}) {
  const openSourceCall = useOpenSourceCall()
  return (
    <button
      type="button"
      data-testid="next-step-from-call"
      onClick={openSourceCall ? () => openSourceCall(voiceCallId) : undefined}
      disabled={!openSourceCall}
      className="flex items-center gap-1 w-fit pl-8 text-[#B86A2D] text-[11px] leading-3.5 hover:underline disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
    >
      <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M8 1C8 4 9 7 12 7C15 7 8 8 8 8C8 8 15 9 12 9C9 9 8 12 8 15C8 12 7 9 4 9C1 9 8 8 8 8C8 8 1 7 4 7C7 7 8 4 8 1Z"
          fill="#B86A2D"
        />
      </svg>
      <span className="underline"><Trans>from your call {label}</Trans></span>
    </button>
  )
}

function CompleteToggle({
  deal,
  step,
}: {
  deal: {id: number}
  step: DealDrawerPendingNextStep
}) {
  const { t } = useLingui()
  const onClick = () => {
    router.patch(
      `/v2/deals/${deal.id}/activities/${step.id}`,
      {activity: {status: "completed"}},
      {preserveScroll: true},
    )
  }
  return (
    <button
      type="button"
      aria-label={t`Mark ${step.title ?? t`next step`} complete`}
      onClick={onClick}
      data-testid={`next-step-complete-${step.id}`}
      className="size-5 shrink-0 mt-px rounded-full border border-[#C9C2B6] bg-white hover:bg-[#F0E8C9] hover:border-[#B86A2D]"
    />
  )
}

// Tiny method indicator — coloured pictogram next to the
// due label. The full method set lives on Activity (see
// `app/models/activity.rb`); the picker is intentionally
// narrow today (WhatsApp / Call / Email) because those
// cover every seeded next-step and every Paper artboard.
function MethodGlyph({method}: {method: string | null}) {
  if (!method) return null
  if (method.startsWith("whatsapp")) {
    return (
      <span className="inline-flex items-center gap-1">
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M8 2C4.69 2 2 4.69 2 8C2 9.16 2.34 10.24 2.91 11.16L2 14L4.94 13.13C5.83 13.67 6.88 14 8 14C11.31 14 14 11.31 14 8C14 4.69 11.31 2 8 2Z"
            fill="#25D366"
          />
        </svg>
        <span>WhatsApp</span>
      </span>
    )
  }
  if (method.startsWith("voice_call") || method === "voice_call") {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-[12px] leading-4">📞</span>
        <span><Trans>Call</Trans></span>
      </span>
    )
  }
  if (method.startsWith("email") || method === "email") {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-[12px] leading-4">✉︎</span>
        <span><Trans>Email</Trans></span>
      </span>
    )
  }
  return null
}

function PlusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3V13M3 8H13" stroke="#1E1B17" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function EditPencilIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}
